import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryTags } from "src/queries/tags/tags.query";
import { useSearchStorage } from "src/storage/useSearchStorage";
import { MIN_SEARCH_LENGTH } from "./AssetsFilterTags.constants";
import {
  calculateOpenFamilies,
  filterRelevantTags,
  groupTagsByFamily,
  shouldUpdateOpenFamilies,
} from "./AssetsFilterTags.utils";

export const useAssetsFilterTags = () => {
  const { appTags = [], setAppTags } = useSearchStorage();
  const { data: allTags, isFetched } = useQueryTags();

  const validAppTags = useMemo(() => {
    if (!allTags) return [];
    return appTags.filter((tagId) => allTags.some((t) => t.tag_id === tagId));
  }, [allTags, appTags]);

  useEffect(() => {
    if (isFetched && validAppTags.length !== appTags.length) {
      setAppTags(validAppTags);
    }
  }, [validAppTags, appTags, setAppTags, isFetched]);

  return { tags: allTags, appTags: validAppTags, setAppTags };
};

export const useTagSearch = (
  tags: ReturnType<typeof useAssetsFilterTags>["tags"],
  appTags: number[]
) => {
  const [searchText, setSearchText] = useState("");
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const appTagsSet = useMemo(() => new Set(appTags), [appTags]);

  useEffect(() => {
    if (appTags.length === 0 && showSelectedOnly) {
      setShowSelectedOnly(false);
    }
  }, [appTags.length, showSelectedOnly]);

  const relevantTags = useMemo(
    () => filterRelevantTags(tags, appTagsSet, searchText, showSelectedOnly),
    [tags, appTagsSet, searchText, showSelectedOnly]
  );

  const grouping = useMemo(() => groupTagsByFamily(relevantTags), [relevantTags]);

  const resetSearchUi = useCallback(() => {
    setSearchText("");
    setShowSelectedOnly(false);
  }, []);

  return {
    searchText,
    setSearchText,
    showSelectedOnly,
    setShowSelectedOnly,
    resetSearchUi,
    relevantTags,
    appTagsSet,
    ...grouping,
  };
};

export const useTagFamilyExpansion = (
  searchText: string,
  familyNames: string[],
  showSelectedOnly = false
) => {
  const [openFamilies, setOpenFamilies] = useState(new Set<string>());
  const [userCollapsed, setUserCollapsed] = useState(new Set<string>());
  const wasShowSelectedOnly = useRef(false);

  useEffect(() => {
    if (showSelectedOnly && !wasShowSelectedOnly.current) {
      setOpenFamilies(new Set(familyNames));
    }
    wasShowSelectedOnly.current = showSelectedOnly;
  }, [showSelectedOnly, familyNames]);

  // Ignore familyNames to prevent collapse on tag selection
  // biome-ignore lint/correctness/useExhaustiveDependencies: familyNames churn on select must not reset open state
  useEffect(() => {
    if (showSelectedOnly) {
      return;
    }

    if (searchText && searchText.length >= MIN_SEARCH_LENGTH) {
      setOpenFamilies((prev) => {
        const next = calculateOpenFamilies(searchText, familyNames, userCollapsed);
        if (shouldUpdateOpenFamilies(prev, next)) {
          return next;
        }
        return prev;
      });
    } else if (searchText.length === 0) {
      setOpenFamilies((prev) => (prev.size === 0 ? prev : new Set()));
      setUserCollapsed((prev) => (prev.size === 0 ? prev : new Set()));
    }
  }, [searchText, userCollapsed, showSelectedOnly]);

  const toggleFamily = useCallback(
    (familyName: string) => {
      setOpenFamilies((prev) => {
        const next = new Set(prev);
        const isCurrentlyOpen = next.has(familyName);
        next[isCurrentlyOpen ? "delete" : "add"](familyName);

        if (searchText && isCurrentlyOpen) {
          setUserCollapsed((collapsed) => {
            const nextCollapsed = new Set(collapsed);
            nextCollapsed.add(familyName);
            return nextCollapsed;
          });
        }

        return next;
      });
    },
    [searchText]
  );

  return {
    openFamilies,
    toggleFamily,
  };
};

export const useTagSelection = (
  appTags: number[],
  setAppTags: (tags: number[]) => void
) => {
  const toggleTag = useCallback(
    (tagId: number) => {
      const isSelected = appTags.includes(tagId);
      if (isSelected) {
        setAppTags(appTags.filter((id) => id !== tagId));
      } else {
        setAppTags([...appTags, tagId]);
      }
    },
    [appTags, setAppTags]
  );

  const clearAll = useCallback(() => {
    setAppTags([]);
  }, [setAppTags]);

  return {
    toggleTag,
    clearAll,
  };
};
